/**
 * AuditSurfaceDelta — the "two checks collapse to one envelope" visual for
 * Immunity Closure Guarantee explainers.
 *
 * An ICG reduces the audit surface: instead of separately checking the
 * primal guarantee and its dual Structural Duality Principle, an auditor
 * checks one Safety Envelope. This component shows that delta — two
 * independent check chips on the left, one envelope chip on the right.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { GRAY, withAlpha } from "../../design-system/tokens.js";

export interface AuditSurfaceDeltaProps {
  /** Primal check label, e.g. "DATA.LINEAGE witness". */
  readonly primalCheck: string;
  /** Dual check label, e.g. "TRACE witness". */
  readonly dualCheck: string;
  /** Envelope label, e.g. "ICG-Tr envelope". */
  readonly envelopeLabel: string;
  /** Primal accent color (hex). */
  readonly primalAccent: string;
  /** Dual accent color (hex). */
  readonly dualAccent: string;
}

function Chip({
  text,
  accent,
  dashed,
}: {
  readonly text: string;
  readonly accent: string;
  readonly dashed: boolean;
}): React.JSX.Element {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "0.2rem 0.55rem",
        borderRadius: "0.375rem",
        fontSize: "0.75rem",
        fontWeight: 600,
        color: accent,
        backgroundColor: withAlpha(accent, "14"),
        border: `1px ${dashed ? "dashed" : "solid"} ${withAlpha(accent, "66")}`,
      }}
    >
      {text}
    </span>
  );
}

export const AuditSurfaceDelta = React.memo(function AuditSurfaceDelta({
  primalCheck,
  dualCheck,
  envelopeLabel,
  primalAccent,
  dualAccent,
}: AuditSurfaceDeltaProps): React.JSX.Element {
  return (
    <div
      aria-label="Audit-surface delta: two separate checks collapse to one Safety Envelope"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.625rem",
        flexWrap: "wrap",
        marginTop: "0.75rem",
        padding: "0.625rem",
        borderRadius: "0.5rem",
        backgroundColor: GRAY[100],
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
        <span
          style={{
            fontSize: "0.625rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: GRAY[500],
          }}
        >
          Before · two checks
        </span>
        <Chip text={primalCheck} accent={primalAccent} dashed />
        <Chip text={dualCheck} accent={dualAccent} dashed />
      </div>

      <div aria-hidden="true" style={{ fontSize: "1.25rem", color: GRAY[500], fontWeight: 700 }}>
        ⟶
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
        <span
          style={{
            fontSize: "0.625rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: GRAY[500],
          }}
        >
          After · one envelope
        </span>
        <span
          style={{
            display: "inline-block",
            padding: "0.3rem 0.65rem",
            borderRadius: "0.375rem",
            fontSize: "0.8125rem",
            fontWeight: 700,
            color: GRAY[900],
            backgroundImage: `linear-gradient(135deg, ${withAlpha(
              primalAccent,
              "33",
            )} 0%, ${withAlpha(dualAccent, "33")} 100%)`,
            border: `1px solid ${GRAY[400]}`,
          }}
        >
          {envelopeLabel}
        </span>
      </div>
    </div>
  );
});
